import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import './App.css';

// Pages
import LoginPage from './pages/LoginPage';
import FormPage from './pages/FormPage';
import FormSuccessPage from './pages/FormSuccessPage';
import DashboardPage from './pages/DashboardPage';
import PillaresPage from './pages/PillaresPage';
import ChatPage from './pages/ChatPage';
import MetasPage from './pages/MetasPage';
import AdminUsersPage from './pages/AdminUsersPage';
import AdminQuestionsPage from './pages/AdminQuestionsPage';
import AdminEliosPage from './pages/AdminEliosPage';
import AdminMentoradosPage from './pages/AdminMentoradosPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import NPSFillPage from './pages/NPSFillPage';
import NpsHistory from './pages/NpsHistory';
import AdminNpsDashboard from './pages/AdminNpsDashboard';
import GamificationFillPage from './pages/GamificationFillPage';
import AdminGamificationRankingPage from './pages/AdminGamificationRankingPage';
import AdminGameRankingPage from './pages/AdminGameRankingPage';
import AdminGameAuditPage from './pages/AdminGameAuditPage';
import AdminEventPointsPage from './pages/AdminEventPointsPage';
import GamificationRankingPage from './pages/GamificationRankingPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import WeeklyProgressPage from './pages/WeeklyProgressPage';
import AdminWeeklyProgressPage from './pages/AdminWeeklyProgressPage';
import AdminObservabilityPage from './pages/AdminObservabilityPage';
import AdminMagnusPage from './pages/AdminMagnusPage';
import AdminJobsPage from './pages/AdminJobsPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import PlaybookPage from './pages/PlaybookPage';

// Protected Route Component
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  if (adminOnly && !isAdmin()) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/ranking-tv" element={<AdminGamificationRankingPage publicView />} />
      <Route
        path="/"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/esqueci-senha"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route path="/form" element={<FormPage />} />
      <Route path="/form/success" element={<FormSuccessPage />} />
      <Route path="/nps/:npsId" element={<NPSFillPage />} />
      <Route path="/gamificacao/:gamificationId" element={<GamificationFillPage />} />
      <Route path="/politica-de-privacidade" element={<PrivacyPolicyPage />} />
      <Route path="/playbook" element={<PlaybookPage />} />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pilares"
        element={
          <ProtectedRoute>
            <PillaresPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/chat"
        element={
          <ProtectedRoute>
            <ChatPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/metas"
        element={
          <ProtectedRoute>
            <MetasPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/historico-nps"
        element={
          <ProtectedRoute>
            <NpsHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/weekly-progress"
        element={
          <ProtectedRoute>
            <WeeklyProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ranking"
        element={
          <ProtectedRoute>
            <GamificationRankingPage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin/usuarios"
        element={
          <ProtectedRoute adminOnly>
            <AdminUsersPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/perguntas"
        element={
          <ProtectedRoute adminOnly>
            <AdminQuestionsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/elios"
        element={
          <ProtectedRoute adminOnly>
            <AdminEliosPage />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/nps"
        element={
          <ProtectedRoute adminOnly>
            <AdminNpsDashboard />
          </ProtectedRoute>
        }
      />

      <Route
        path="/admin/mentorados"
        element={
          <ProtectedRoute adminOnly>
            <AdminMentoradosPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/configuracoes"
        element={
          <ProtectedRoute adminOnly>
            <AdminSettingsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/weekly-progress"
        element={
          <ProtectedRoute adminOnly>
            <AdminWeeklyProgressPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/observabilidade"
        element={
          <ProtectedRoute adminOnly>
            <AdminObservabilityPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/magnus"
        element={
          <ProtectedRoute adminOnly>
            <AdminMagnusPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/jobs"
        element={
          <ProtectedRoute adminOnly>
            <AdminJobsPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/gamificacao"
        element={
          <ProtectedRoute adminOnly>
            <AdminGamificationRankingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/game-ranking"
        element={
          <ProtectedRoute adminOnly>
            <AdminGameRankingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/game-audit"
        element={
          <ProtectedRoute adminOnly>
            <AdminGameAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/eventos"
        element={
          <ProtectedRoute adminOnly>
            <AdminEventPointsPage />
          </ProtectedRoute>
        }
      />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster 
            position="top-right"
            toastOptions={{
              style: {
                background: '#112240',
                color: '#f8fafc',
                border: '1px solid rgba(255,255,255,0.1)',
              },
            }}
          />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
